import type { AxiosError } from 'axios'
import { apiClient } from './client'

/**
 * Модуль «Пополнить словарь»: учитель прогоняет свой словарь с учеником,
 * оценивает слова (быстрый вердикт или пошкально), по оценкам создаются
 * карточки ученика с начальным прогрессом. Премиум-параметр подписки
 * dictionary_fill_enabled гейтит только действия (preview/create/next-batch/
 * assess); просмотр, complete и cancel доступны без него.
 */

export type DictionaryFillVerdict = 'known_well' | 'known' | 'unsure' | 'unknown' | 'skip'

export type DictionaryFillRunStatus = 'active' | 'completed' | 'cancelled'

export type DictionaryFillSkillKey =
  | 'translation'
  | 'reverse_translation'
  | 'spelling'
  | 'listening'
  | 'pronunciation'

export const DICTIONARY_FILL_SKILLS: DictionaryFillSkillKey[] = [
  'translation',
  'reverse_translation',
  'spelling',
  'listening',
  'pronunciation',
]

export type DictionaryFillWord = {
  id: number
  text: string
  /** null для ученика — перевод скрыт, прогон проверяет знание */
  translation: string | null
  transcription: string | null
}

export type DictionaryFillItem = {
  id: number
  run_id: number
  word_id: number
  order: number
  verdict: DictionaryFillVerdict | null
  skills_override: Partial<Record<DictionaryFillSkillKey, number>> | null
  card_id: number | null
  revised_count: number
  assessed_at: string | null
  word: DictionaryFillWord | null
}

export type DictionaryFillVerdictSummary = {
  known_well: number
  known: number
  unsure: number
  unknown: number
  skip: number
  pending: number
}

export type DictionaryFillRun = {
  id: number
  teacher_id: number
  student_id: number
  lesson_id: number | null
  status: DictionaryFillRunStatus
  status_label: string
  dictionary_id: number | null
  tag_ids: number[] | null
  word_type: 'word' | 'phrase' | null
  word_limit: number | null
  words_total_estimate: number
  items_assessed: number
  cards_created: number
  started_at: string | null
  completed_at: string | null
  created_at: string | null
  student?: { id: number; name: string }
  teacher?: { id: number; name: string }
  items?: DictionaryFillItem[]
  verdict_summary?: DictionaryFillVerdictSummary
}

/** Счётчики подборки по одному участнику урока. */
export type DictionaryFillStudentPreview = {
  student_id: number
  student_name: string
  words_available: number
  words_skipped_existing_cards: number
}

export type DictionaryFillFilters = {
  tag_ids?: number[]
  dictionary_id?: number | null
  word_type?: 'word' | 'phrase' | null
}

export type CreateDictionaryFillRunPayload = DictionaryFillFilters & {
  word_limit?: number | null
}

export type AssessDictionaryFillItemPayload = {
  verdict: DictionaryFillVerdict
  skills?: Partial<Record<DictionaryFillSkillKey, number>>
}

export type ListDictionaryFillRunsParams = {
  student_id?: number
  lesson_id?: number
  status?: DictionaryFillRunStatus
  page?: number
  per_page?: number
}

export type PaginatedRuns = {
  data: DictionaryFillRun[]
  current_page: number
  last_page: number
  per_page: number
  total: number
}

type ApiResponse<T> = {
  success?: boolean
  message?: string
  data?: T
  meta?: Record<string, unknown>
}

function unwrapResource<T>(raw: ApiResponse<T> | T): T {
  if (raw && typeof raw === 'object' && 'data' in raw) {
    return (raw as ApiResponse<T>).data as T
  }
  return raw as T
}

/**
 * Пробрасывает ошибку с кодом и параметром подписки — страница показывает
 * CTA «Перейти к подписке» при code === 'subscription_parameter_denied'.
 *
 * message не подменяется хардкод-текстом: без message от API страница сама
 * подставляет локализованный fallback (t('common.error')) — иначе в русском
 * UI при 500/сетевом сбое всплывал бы английский текст.
 */
export type DictionaryFillApiError = Error & {
  status?: number
  code?: string
  parameter?: string
}

function rethrowRich(err: unknown): never {
  const axiosErr = err as AxiosError<{ message?: string; error?: string; parameter?: string }>
  const data = axiosErr.response?.data
  const rich = new Error(data?.message ?? '') as DictionaryFillApiError
  if (axiosErr.response?.status !== undefined) rich.status = axiosErr.response.status
  if (data?.error) rich.code = data.error
  if (data?.parameter) rich.parameter = data.parameter
  throw rich
}

export const dictionaryFillApi = {
  /** Счётчики по каждому участнику урока (создание — только из урока). */
  previewForLesson: async (
    lessonId: number,
    params: DictionaryFillFilters,
  ): Promise<DictionaryFillStudentPreview[]> => {
    try {
      const response = await apiClient.get<ApiResponse<{ students: DictionaryFillStudentPreview[] }>>(
        `/lessons/${lessonId}/dictionary-fill/preview`,
        {
          params,
          // indexes: false → tag_ids[]=1&tag_ids[]=2 (Laravel ждёт массив;
          // indexes: null давал tag_ids=1 без скобок → 422 «должно быть массивом»)
          paramsSerializer: {
            indexes: false,
          },
        },
      )
      return unwrapResource(response.data).students ?? []
    } catch (err) {
      rethrowRich(err)
    }
  },

  /** Создать прогоны из урока: по одному на участника с доступными словами. */
  createForLesson: async (
    lessonId: number,
    payload: CreateDictionaryFillRunPayload,
  ): Promise<DictionaryFillRun[]> => {
    try {
      const response = await apiClient.post<ApiResponse<DictionaryFillRun[]>>(
        `/lessons/${lessonId}/dictionary-fill`,
        payload,
      )
      return unwrapResource(response.data) ?? []
    } catch (err) {
      rethrowRich(err)
    }
  },

  list: async (params: ListDictionaryFillRunsParams = {}): Promise<PaginatedRuns> => {
    try {
      const response = await apiClient.get<{
        data?: DictionaryFillRun[]
        meta?: { current_page?: number; last_page?: number; per_page?: number; total?: number }
      }>('/dictionary-fill', { params })
      const meta = response.data.meta ?? {}
      return {
        data: response.data.data ?? [],
        current_page: meta.current_page ?? 1,
        last_page: meta.last_page ?? 1,
        per_page: meta.per_page ?? 15,
        total: meta.total ?? 0,
      }
    } catch (err) {
      rethrowRich(err)
    }
  },

  get: async (runId: number): Promise<DictionaryFillRun> => {
    try {
      const response = await apiClient.get<ApiResponse<DictionaryFillRun>>(`/dictionary-fill/${runId}`)
      return unwrapResource(response.data)
    } catch (err) {
      rethrowRich(err)
    }
  },

  nextBatch: async (
    runId: number,
    size?: number,
  ): Promise<{ items: DictionaryFillItem[]; has_more: boolean; items_total: number }> => {
    try {
      const response = await apiClient.post<{
        data?: DictionaryFillItem[]
        meta?: { has_more?: boolean; items_total?: number }
      }>(`/dictionary-fill/${runId}/next-batch`, size !== undefined ? { size } : {})
      return {
        items: response.data.data ?? [],
        has_more: Boolean(response.data.meta?.has_more),
        items_total: response.data.meta?.items_total ?? 0,
      }
    } catch (err) {
      rethrowRich(err)
    }
  },

  assess: async (
    runId: number,
    itemId: number,
    payload: AssessDictionaryFillItemPayload,
  ): Promise<{
    item: DictionaryFillItem
    run_status: DictionaryFillRunStatus
    items_assessed: number
    cards_created: number
  }> => {
    try {
      const response = await apiClient.post<{
        data?: DictionaryFillItem
        meta?: { run_status?: DictionaryFillRunStatus; items_assessed?: number; cards_created?: number }
      }>(`/dictionary-fill/${runId}/items/${itemId}/assess`, payload)
      const item = response.data.data
      if (!item || typeof item.id !== 'number') {
        // Неожиданная форма ответа: бросаем управляемую ошибку вместо падения в рендере.
        throw new Error('')
      }
      const meta = response.data.meta ?? {}
      return {
        item,
        run_status: meta.run_status ?? 'active',
        items_assessed: meta.items_assessed ?? 0,
        cards_created: meta.cards_created ?? 0,
      }
    } catch (err) {
      rethrowRich(err)
    }
  },

  complete: async (runId: number): Promise<DictionaryFillRun> => {
    try {
      const response = await apiClient.post<ApiResponse<DictionaryFillRun>>(`/dictionary-fill/${runId}/complete`)
      return unwrapResource(response.data)
    } catch (err) {
      rethrowRich(err)
    }
  },

  cancel: async (runId: number): Promise<DictionaryFillRun> => {
    try {
      const response = await apiClient.post<ApiResponse<DictionaryFillRun>>(`/dictionary-fill/${runId}/cancel`)
      return unwrapResource(response.data)
    } catch (err) {
      rethrowRich(err)
    }
  },
}
